/**
 * DiffSegmented — two-option segmented toggle for the Diff sub-mode header.
 *
 * Design refs:
 *   design.md §9.1 — two segmented toggles: semantic|source and inline|side-by-side.
 *   design.md §4.4 — --radius-segmented / --radius-segmented-inner.
 *   design.md §2   — one accent, reserved; no shadows except permitted CTA shadow.
 *   design.md §4.5 — 200 ms ease state transitions.
 *
 * Accessibility: role="group" + aria-label on the wrapper;
 * each option is a <button> with aria-pressed.
 *
 * No banned words in this component (branding.md §5.2.1).
 */

import { useEffect, type CSSProperties } from "react";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** When true the option is visible but non-interactive. */
  disabled?: boolean;
  /** Optional per-option data-testid (for e2e / component tests). */
  testId?: string;
}

export interface DiffSegmentedProps<T extends string> {
  /** Aria-label for the group (e.g. "Diff type" or "Diff layout"). */
  groupLabel: string;
  options: [SegmentedOption<T>, SegmentedOption<T>];
  value: T;
  onChange: (value: T) => void;
  /** Optional group-level data-testid. */
  testId?: string;
}

// ── CSS ───────────────────────────────────────────────────────────────────────

const SEGMENTED_CSS = `
  .proof-diff-seg {
    display: inline-flex;
    align-items: center;
    height: 28px;
    border: 1px solid var(--line);
    border-radius: var(--radius-segmented);
    background: var(--bg);
    padding: 2px;
    gap: 2px;
  }

  .proof-diff-seg__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 22px;
    padding: 0 var(--space-2);
    border: 1px solid transparent;
    border-radius: var(--radius-segmented-inner);
    background: transparent;
    font-family: var(--font-sans);
    font-size: 12px;
    color: var(--muted);
    cursor: pointer;
    white-space: nowrap;
    transition: color var(--motion-state), background var(--motion-state), border-color var(--motion-state);
    -webkit-tap-highlight-color: transparent;
    min-width: var(--tap-target-min);
    /* tabular-nums not needed for label text — only for numerics */
  }

  .proof-diff-seg__btn:hover:not(:disabled) {
    color: var(--text);
  }

  /* Keyboard a11y — accent focus ring on the segmented control (design.md §2:
     one accent reserved for signal). Mouse focus stays ring-free via :focus-visible. */
  .proof-diff-seg__btn:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -2px;
  }

  .proof-diff-seg__btn[aria-pressed="true"] {
    background: var(--surface);
    color: var(--text);
    font-weight: 500;
    /* accent border only on the active segment — satisfies one-accent rule (design.md §2) */
    border-color: var(--accent);
  }

  .proof-diff-seg__btn:disabled {
    cursor: not-allowed;
    /* Explicit disabled color instead of opacity-collapse. Transparent ghost
       segment, so no fill change — --cta-disabled-fg reads 5.07:1 on --bg /
       5.38:1 on --surface (legible) while sitting below the enabled --muted /
       --text states, so it still reads disabled. */
    color: var(--cta-disabled-fg);
  }

  /* ── Mobile tap-target floor (Rule 7 / WCAG 2.5.5) ──────────────────────
     On phones each 22px option is below the 44px touch minimum. Floor the
     option height and grow the container to fit; desktop density (28px
     container / 22px option) is preserved above the breakpoint. */
  @media (max-width: 767px) {
    .proof-diff-seg {
      height: auto;
      min-height: var(--tap-target-min, 44px);
    }
    .proof-diff-seg__btn {
      min-height: var(--tap-target-min, 44px);
      height: auto;
    }
  }
`;

// ── Component ─────────────────────────────────────────────────────────────────

export function DiffSegmented<T extends string>({
  groupLabel,
  options,
  value,
  onChange,
  testId,
}: DiffSegmentedProps<T>) {
  useEffect(() => {
    const id = "proof-diff-seg-css";
    if (typeof document !== "undefined" && document.getElementById(id) === null) {
      const el = document.createElement("style");
      el.id = id;
      el.textContent = SEGMENTED_CSS;
      document.head.appendChild(el);
    }
  }, []);

  const containerStyle: CSSProperties = {};

  return (
    <div
      role="group"
      aria-label={groupLabel}
      className="proof-diff-seg"
      style={containerStyle}
      {...(testId !== undefined ? { "data-testid": testId } : {})}
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className="proof-diff-seg__btn"
          aria-pressed={value === opt.value}
          disabled={opt.disabled === true}
          onClick={() => {
            if (!opt.disabled) onChange(opt.value);
          }}
          {...(opt.testId !== undefined ? { "data-testid": opt.testId } : {})}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
